import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { BehaviorSubject, Subject, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { Profile, UserId } from './profile.model';
import { Member } from './member.model';
import { LogService } from '../../services/log.service';
import { LimitedMember } from '../../models/limited-member';
import { MemberConfiguration } from './member-configuration.model';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  apiHost: string = environment.apiHost;
  public isLoading: boolean = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private logService: LogService
  ) {}

  getProfile(userId: UserId): Promise<Profile> { 
    let promise = new Promise<Profile>((resolve, reject) => {
      return this.http
        .post<Profile>(this.apiHost + '/user/getUser', userId)
        .toPromise()
        .then(
          (data) => {
            resolve(data);  
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Get Profile', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  setProfile(profile: Profile) {
    let promise = new Promise<Profile>((resolve, reject) => {
      return this.http
        .post<Profile>(this.apiHost + '/user', profile)
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Set Profile', msg);
            reject(msg);
          }
        );
    });
    return promise;
  } 

  getUserIdByPhoneNumber(
    phoneNumber: string
  ): Promise<string> {
    let promise = new Promise<string>((resolve, reject) => {
      return this.http 
        .get<string>(
          this.apiHost +
            '/Member/GetUserIdByPhoneNumber?PhoneNumber=' +
            phoneNumber 
        ) 
        .toPromise()
        .then(
          (data) => { 
            resolve(data);
          },
          (msg) => {
            resolve("");
          }
        );
    });
    return promise;
  }  

  getMemberConfiguration(): Promise<MemberConfiguration[]> {
    let promise = new Promise<MemberConfiguration[]>((resolve, reject) => {
      return this.http 
        .get<MemberConfiguration[]>(
          this.apiHost +
            '/Member/GetMemberConfiguration' 
        ) 
        .toPromise()
        .then(
          (data) => { 
            resolve(data);
          },
          (msg) => {
            resolve(null);
          }
        );
    });
    return promise;
  }   

  getLimitedMembers(
    userId: string
  ): Promise<LimitedMember[]> {
    let promise = new Promise<LimitedMember[]>((resolve, reject) => {
      return this.http
        .get<LimitedMember[]>(
          this.apiHost +
            '/Member/GetLimitedMembersByUserId?UserId=' +
            userId 
        ) 
        .toPromise()
        .then(
          (data) => { 
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Get Limited Members', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  getMembers(
    userId: string,
    AuthorId: string,
    limited: boolean
  ): Promise<Member[]> {
    let promise = new Promise<Member[]>((resolve, reject) => {
      return this.http
        .get<Member[]>(
          this.apiHost +
            '/Member/GetMemberByUserId?UserId=' +
            userId +
            '&Author=' +
            AuthorId +
            '&Limited=' +
            limited
        ) 
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Get Members', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  getMember(
    userId: string
  ): Promise<Member> {
    let promise = new Promise<Member>((resolve, reject) => {
      return this.http
        .get<Member>(
          this.apiHost +
            '/Member/GetMemberByUserId2?UserId=' +
            userId
        ) 
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Get Member', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }  

  addMember(userId: string, memberList: Member[]): Promise<Member[]> {
    let promise = new Promise<Member[]>((resolve, reject) => {
      return this.http
        .post<Member[]>(
          this.apiHost + '/Member/AddMembersToUser?UserId=' + userId,
          memberList
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Add Member', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  getRecommendation(userId: string, member: Member) {
    let promise = new Promise<Member>((resolve, reject) => {
      return this.http
        .post<Member>(
          this.apiHost + '/Member/GetRecommendation?UserId=' + userId,
          member
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Get Recommendation', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  unsubscribe(userId: string) {
    let promise = new Promise<boolean>((resolve, reject) => {
      return this.http
      .post<Profile>(this.apiHost + '/user/unsubscribe?UserId=' + userId, null)
        .toPromise()
        .then(
          (data) => {
            resolve(true); 
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Unsubscribe Account', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }  

  saveMember(userId: string, member: Member): Promise<Member> {
    let promise = new Promise<Member>((resolve, reject) => {
      return this.http
        .post<Member>(
          this.apiHost + '/Member/SaveMember?UserId=' + userId,
          member
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Save Member', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  saveAccountMember(userId: string, member: Member): Promise<Member> {
    let promise = new Promise<Member>((resolve, reject) => {
      return this.http
        .post<Member>(
          this.apiHost + '/Member/SaveAccountMember?UserId=' + userId,
          member
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: SaveAccountMember', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  deleteMember(userId, member: Member[]): Promise<string> {
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(
          this.apiHost + '/Member/RemoveMembersFromUser?UserId=' + userId,
          member
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Delete Member', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  requestMembership(userId: string, members: number[]) {
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(
          this.apiHost + '/Membership/RequestMembership?UserId=' + userId,
          members
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Request Membership', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  cancelMembership(userId: string, members: number[]) {
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(
          this.apiHost + '/Membership/CancelMembership?UserId=' + userId,
          members
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Cancel Membership', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  acceptRequest(memberId: number) {
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(this.apiHost + '/Membership/ApproveByMember', memberId)
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Accept Request', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  denyRequest(memberId: number) {
    let promise = new Promise<string>((resolve, reject) => {
      return this.http
        .post<string>(this.apiHost + '/Membership/DenyByMember', memberId)
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            if (this.logService != null)
              this.logService.addToLog('Admin: Deny Request', msg);
            reject(msg);
          }
        );
    });
    return promise;
  }

  async sendCodeByPhone(phoneNumber: string): Promise<{ message: string }> {
    phoneNumber = phoneNumber.replace('+','').trim();
    let promise = new Promise<{ message: string }>(async (resolve, reject) => {
      return await this.http
        .post<{ message: string }>(
          this.apiHost + '/PhoneVerification/SendRequestByPhone?PhoneNumber=' + phoneNumber,
          null
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            resolve(msg);
          }
        );
    });
    return promise;
  }

  async VerifyPhoneNumberByRequestId(phoneNumber: string, code: string): Promise<{ message: string }> {
    phoneNumber = phoneNumber.replace('+','').trim();
    let promise = new Promise<{ message: string }>(async (resolve, reject) => {
      return await this.http
        .post<{ message: string }>(
          this.apiHost +
            '/PhoneVerification/VerifyPhoneNumberByRequestId?phoneNumber=' +
            phoneNumber +
            '&Code=' +
            code,
          null
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            resolve(msg);
          }
        );
    });
    return promise;
  }

  async sendCode(userId: string): Promise<{ message: string }> {
    let promise = new Promise<{ message: string }>(async (resolve, reject) => {
      return await this.http
        .post<{ message: string }>(
          this.apiHost + '/PhoneVerification/sendrequest?UserId=' + userId,
          null
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            resolve(msg);
          }
        );
    });
    return promise;
  }

  async resendActivationEmail(userId: string): Promise<{ message: string }> {
    let promise = new Promise<{ message: string }>(async (resolve, reject) => {
      return await this.http
        .post<{ message: string }>(
          this.apiHost + '/user/resendActivationEmail?UserId=' + userId,
          null
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            resolve(msg);
          }
        );
    });
    return promise;
  }  

  async verifyCode(userId: string, code: string): Promise<{ message: string }> {
    let promise = new Promise<{ message: string }>(async (resolve, reject) => {
      return await this.http
        .post<{ message: string }>(
          this.apiHost +
            '/PhoneVerification/VerifyPhone?UserId=' +
            userId +
            '&Code=' +
            code,
          null
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data);
          },
          (msg) => {
            resolve(msg);
          }
        );
    });
    return promise;
  }

  async isPending(userId: string): Promise<boolean> {
    let promise = new Promise<boolean>(async (resolve, reject) => {
      return await this.http
        .post<{ message: string }>(
          this.apiHost + '/PhoneVerification/isPending?UserId=' + userId,
          null
        )
        .toPromise()
        .then(
          (data) => {
            resolve(data.message == '');
          },
          (msg) => {
            resolve(false);
          }
        );
    });
    return promise;
  }
}
